import Cookies from 'js-cookie';
import { EVENT_OPEN } from './InfoBox';

const DAYS_IN_YEAR = 365;
const KEY_COOKIES_SETTINGS = 'ds_cookies_settings';
const CLASS_MEDIA_OVERLAY = 'cookies-info-box-overlay';

function cloneScriptTag(script: HTMLScriptElement): HTMLScriptElement {
  const clonedScript: HTMLScriptElement = document.createElement('script');

  for (let i = 0; i < script.attributes.length; i++) {
    clonedScript.setAttribute(script.attributes[i].name, script.attributes[i].value);
  }

  if (script.text) {
    clonedScript.text = script.text;
  }

  clonedScript.setAttribute('type', 'text/javascript');

  return clonedScript;
}

export default class DsCookies {
  categories: { [key: string]: boolean } = {};

  constructor() {
    const cookiesSettings = Cookies.get(KEY_COOKIES_SETTINGS);

    try {
      const cookiesSettingsObject = JSON.parse(cookiesSettings || '{}');

      Object.keys(cookiesSettingsObject).forEach((category) => {
        this.categories[category] = cookiesSettingsObject[category];
      });
    } catch (e) {
      // do nothing
    }

    this.run();
  }

  isElementAllowed(element:HTMLElement):boolean {
    const elementCookieSettings = element.dataset.cookieconsent?.split(',').map(category => category.trim());

    if (elementCookieSettings) {
      return elementCookieSettings.every(category => this.categories[category]);
    }

    return true;
  }

  isSaved():boolean {
    return !!Cookies.get(KEY_COOKIES_SETTINGS);
  }

  async disableIFrames(scope: ParentNode = document) {
    const openCookiesBtn = document.createElement('button');
    openCookiesBtn.innerHTML = window.AlpineI18n.t('cookies.info.disabled');
    openCookiesBtn.classList.add('btn', 'btn-primary', `${CLASS_MEDIA_OVERLAY}-btn`);
    const divTag = document.createElement('div');
    divTag.classList.add(CLASS_MEDIA_OVERLAY);
    divTag.appendChild(openCookiesBtn)

    await scope.querySelectorAll('iframe')
      .forEach((iframe) => {
        if (iframe.dataset.cookieconsent === 'ignore') {
          return;
        }

        if (!this.isElementAllowed(iframe)) {
          const clonedDiv = divTag.cloneNode(true) as HTMLElement;
          
          const backgroundImage = iframe.dataset.backgroundImage;
          if (backgroundImage) {
            clonedDiv.style.backgroundImage = `url(${backgroundImage})`;
            clonedDiv.style.backgroundSize = 'cover';
            clonedDiv.style.backgroundPosition = 'center';
            clonedDiv.style.backgroundRepeat = 'no-repeat';
          }
          
          iframe.parentNode?.insertBefore(clonedDiv, iframe);
          iframe.style.display = 'none';

          document.addEventListener('click', (event) => {
            const target = event.target as HTMLElement;

            if (target) {
              const isTargetDivTag = target.classList.contains(`${CLASS_MEDIA_OVERLAY}-btn`);

              if (isTargetDivTag) {
                document.dispatchEvent(new Event(EVENT_OPEN));
              }
            }
          });
        }
      });
  }

  enableIFrames(scope: ParentNode = document) {
    scope.querySelectorAll(`.${CLASS_MEDIA_OVERLAY}`)
      .forEach((message) => {
        message.parentNode?.removeChild(message);
      });


    scope.querySelectorAll('iframe')
      .forEach((iframe) => {
        if (this.isElementAllowed(iframe)) {
          if (iframe.dataset.src && !iframe.src) {
            iframe.src = iframe.dataset.src;
          }
          iframe.style.display = 'block';
        }
      });
  }

  enableScripts(scope: Element | Document = document) {
    for (const script of Array.from(scope.getElementsByTagName('script'))) {
      if (this.isElementAllowed(script)) {
        if (script.type.toLowerCase() === 'text/plain') {
          const parent = script.parentNode;
          const sibling = script.nextElementSibling;
          const clonedScript = cloneScriptTag(script) as HTMLScriptElement;
          const newType = clonedScript.getAttribute('data-type');
          if (newType) {
            clonedScript.setAttribute('type', newType);
            clonedScript.removeAttribute('data-type');
          }

          if (parent) {
            parent.removeChild(script);
            parent.insertBefore(clonedScript, sibling || null);
          }
        }
      }
    }
  }

  save(categories: { [key: string]: boolean }, expireDays: number = DAYS_IN_YEAR) {
    this.categories = categories;

    Cookies.set(KEY_COOKIES_SETTINGS, JSON.stringify({
      ...categories,
      date: new Date().toISOString()
    }), {
      expires: expireDays,
      sameSite: 'strict',
      secure: true
    });

    this.run();
  }

  run(scope: Element | Document = document) {
    this.enableScripts(scope);

    this.enableIFrames(scope); // Must be called before disable IFrames
    this.disableIFrames(scope);
  }
}
